<script setup lang="ts" generic="TValue">
import { computed, inject, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import BcButton from "./bc-button.vue";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
} from "../models";

export type BcListBoxProps<TProp> = {
  /** Allows item deletion */
  allowDelete?: boolean;
  /** Allows changing order of items */
  allowOrder?: boolean;
  /** Allows item selection */
  allowSelect?: boolean;
  /** Allows unselection on Escape */
  allowUnselect?: boolean;
  /** Specifies whether the first item should be selected automatically */
  autoSelect?: boolean;
  /** Sets a border around list container */
  bordered?: boolean;
  /** Specifies custom check for disabled items */
  checkDisabled?: (item: TProp) => boolean;
  /** Specifies custom check for read-only items */
  checkReadonly?: (item: TProp) => boolean;
  /** Specifies custom check for inactive items */
  checkInactive?: (item: TProp) => boolean;
  /** Lowers the list size (removes item padding) */
  dense?: boolean;
  /** Disables the list for selection */
  disabled?: boolean;
  /** Expands the list to the width of its parent container */
  fullWidth?: boolean;
  /** Expands the list to the height of its parent container */
  fullHeight?: boolean;
  /** Specifies custom calculation for item key */
  getKey?: (item: TProp) => string;
  /** Specifies custom calculation for item text */
  getText?: (item: TProp) => string;
  /** Sets a divider between the items */
  itemDivider?: boolean;
  /** Specifies the disabled property (e.g. disabled) for disabel item selection if items are objects */
  itemDisabledProperty?: keyof TProp;
  /** Specifies the inactive property (e.g. readonly) to make item inactive styled if items are objects */
  itemInactiveProperty?: keyof TProp;
  /** Specifies the key property (e.g. ID) for identification if items are objects */
  itemKeyProperty?: keyof TProp;
  /** Specifies the text property (e.g. name) for display if items are objects */
  itemTextProperty?: keyof TProp;
  /** Specifies the read-only property (e.g. readonly) to make item read-only if items are objects */
  itemReadonlyProperty?: keyof TProp;
  /** Specifies the list items */
  items: TProp[];
  /** Specifies the number of visible items */
  numberOfVisibleItems?: number;
  /** Makes the list border rounded */
  rounded?: boolean;
  /** Removes background color from hovered and selected items */
  outlined?: boolean;
  /** Makes list read-only */
  readonly?: boolean;
};

export type BcListBoxEmits<TEmit> = {
  (event: "delete", payload: TEmit): void;
  (event: "select", payload: TEmit): void;
};

const {
  allowDelete = false,
  allowOrder = false,
  allowSelect = false,
  allowUnselect = false,
  autoSelect = false,
  bordered = false,
  checkDisabled,
  checkReadonly,
  checkInactive,
  dense = false,
  disabled = false,
  fullWidth = false,
  fullHeight = false,
  getKey,
  getText,
  itemDivider = false,
  itemDisabledProperty,
  itemInactiveProperty,
  itemKeyProperty,
  itemTextProperty,
  itemReadonlyProperty,
  items,
  numberOfVisibleItems,
  rounded = false,
  outlined = false,
  readonly = false,
} = defineProps<BcListBoxProps<TValue>>();

/** Specifies the two-way binding (selected item) for the list */
const modelValue = defineModel<TValue>();

const emit = defineEmits<BcListBoxEmits<TValue>>();

const selectedIdx = ref(-1);

const listBoxRef = useTemplateRef("listBoxRef");
const listItemsRef = useTemplateRef("listItemsRef");

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

const selectedItem = computed(() => {
  if (items && selectedIdx.value > -1 && selectedIdx.value < items.length) {
    return items[selectedIdx.value];
  }
});

function getDisabled(item: TValue): boolean {
  if (checkDisabled) {
    const disabled = checkDisabled(item);
    if (typeof disabled === "boolean") {
      return disabled;
    } else {
      console.warn("Custom disabled check function is invalid.");
    }
  } else if (
    typeof item === "object" &&
    item !== null &&
    itemDisabledProperty &&
    itemDisabledProperty in item &&
    typeof item[itemDisabledProperty] === "boolean"
  ) {
    return item[itemDisabledProperty];
  }
  return false;
}

function getReadonly(item: TValue): boolean {
  if (checkReadonly) {
    const readonly = checkReadonly(item);
    if (typeof readonly === "boolean") {
      return readonly;
    } else {
      console.warn("Custom read-only check function is invalid.");
    }
  } else if (
    typeof item === "object" &&
    item !== null &&
    itemReadonlyProperty &&
    itemReadonlyProperty in item &&
    typeof item[itemReadonlyProperty] === "boolean"
  ) {
    return item[itemReadonlyProperty];
  }
  return false;
}

function getInactive(item: TValue): boolean {
  if (checkInactive) {
    const readonly = checkInactive(item);
    if (typeof readonly === "boolean") {
      return readonly;
    } else {
      console.warn("Custom inactive check function is invalid.");
    }
  } else if (
    typeof item === "object" &&
    item !== null &&
    itemInactiveProperty &&
    itemInactiveProperty in item &&
    typeof item[itemInactiveProperty] === "boolean"
  ) {
    return item[itemInactiveProperty];
  }
  return false;
}

function selectItem(idx: number): void {
  if (
    allowSelect &&
    !disabled &&
    idx >= 0 &&
    idx < items?.length &&
    !getDisabled(items[idx]) &&
    selectedIdx.value !== idx
  ) {
    selectedIdx.value = idx;
  }
}

function deleteItem(): void {
  if (
    allowSelect &&
    allowDelete &&
    !disabled &&
    items?.length &&
    !getDisabled(items[selectedIdx.value]) &&
    !readonly &&
    !getReadonly(items[selectedIdx.value])
  ) {
    emit("delete", items[selectedIdx.value]);
    var currentIdx = selectedIdx.value;
    selectedIdx.value = -1;
    nextTick(() => {
      items?.splice(currentIdx, 1);
      selectedIdx.value = currentIdx;
      if (selectedIdx.value === items?.length) {
        moveSelection(-1);
        if (selectedIdx.value === currentIdx) {
          selectedIdx.value = -1;
        }
      } else if (getDisabled(items[selectedIdx.value])) {
        moveSelection(1);
        if (selectedIdx.value === currentIdx) {
          moveSelection(-1);
        }
        if (selectedIdx.value === currentIdx) {
          selectedIdx.value = -1;
        }
      }
    });
  }
}

function changeOrder(event: MouseEvent, idx: number, direction: number): void {
  // prevent item selection
  event.stopImmediatePropagation();
  let item = items?.splice(idx, 1)[0];
  items?.splice(idx + direction, 0, item);
  nextTick(() => {
    if (allowSelect && selectedIdx.value === idx) {
      selectedIdx.value = idx + direction;
    }
  });
}

function moveSelection(direction: number): void {
  if (!allowSelect || !items || disabled) return;

  let newSelectedIdx = selectedIdx.value + direction;
  while (
    newSelectedIdx > -1 &&
    newSelectedIdx < items?.length &&
    getDisabled(items[newSelectedIdx])
  ) {
    newSelectedIdx += direction;
  }
  if (newSelectedIdx > -1 && newSelectedIdx < items?.length) {
    selectedIdx.value = newSelectedIdx;
  }
}

function removeSelection(): void {
  if (!allowSelect || !allowUnselect || disabled || selectedIdx.value === -1) return;
  selectedIdx.value = -1;
}

function calculateKey(item: TValue): string | number | undefined {
  if (getKey) {
    return getKey(item);
  }
  if (
    typeof item === "object" &&
    item !== null &&
    itemKeyProperty &&
    itemKeyProperty in item &&
    (typeof item[itemKeyProperty] === "string" ||
      typeof item[itemKeyProperty] === "number")
  ) {
    return item[itemKeyProperty];
  }
  return undefined;
}

function calculateText(item: TValue): string {
  if (getText) {
    return getText(item);
  }
  if (
    typeof item === "object" &&
    item !== null &&
    itemTextProperty &&
    itemTextProperty in item
  ) {
    return item[itemTextProperty]?.toString() ?? "";
  }
  return item?.toString() ?? "";
}

/** Checks item properties */
function checkItemProperties(): void {
  if (
    itemKeyProperty &&
    items?.some(
      (item) =>
        itemKeyProperty &&
        (typeof item !== "object" ||
          (typeof item === "object" && item !== null && !(itemKeyProperty in item))),
    )
  ) {
    console.error(
      `Item key property '${itemKeyProperty.toString()}' is not available for all items.`,
    );
  }
  if (
    itemKeyProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(itemKeyProperty in modelValue.value)))
  ) {
    console.error(
      `Item key property '${itemKeyProperty.toString()}' is not available in the model.`,
    );
  }

  if (
    !itemTextProperty &&
    !getText &&
    items?.some((item) => typeof item === "object" && item !== null)
  ) {
    console.error("Item text property is not defined for object items.");
  }
  if (
    !itemTextProperty &&
    !getText &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null
  ) {
    console.error("Item text property is not defined for model.");
  }
  if (
    itemTextProperty &&
    items?.some(
      (item) =>
        itemTextProperty &&
        (typeof item !== "object" ||
          (typeof item === "object" && item !== null && !(itemTextProperty in item))),
    )
  ) {
    console.error(
      `Item text property '${itemTextProperty.toString()}' is not available for all items.`,
    );
  }
  if (
    itemTextProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(itemTextProperty in modelValue.value)))
  ) {
    console.error(
      `Item text property '${itemTextProperty.toString()}' is not available in the model.`,
    );
  }

  if (
    itemDisabledProperty &&
    items?.some(
      (item) =>
        itemDisabledProperty &&
        typeof item === "object" &&
        item !== null &&
        itemDisabledProperty in item &&
        item[itemDisabledProperty] !== null &&
        typeof item[itemDisabledProperty] !== "boolean",
    )
  ) {
    console.error("Disabled value is not boolean in one of the items.");
  }
  if (
    itemDisabledProperty &&
    modelValue.value &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null &&
    itemDisabledProperty in modelValue.value &&
    modelValue.value[itemDisabledProperty] !== null &&
    typeof modelValue.value[itemDisabledProperty] !== "boolean"
  ) {
    console.error("Disabled value is not boolean in model.");
  }

  if (
    itemReadonlyProperty &&
    items?.some(
      (item) =>
        itemReadonlyProperty &&
        (typeof item !== "object" ||
          (typeof item === "object" && item !== null && !(itemReadonlyProperty in item))),
    )
  ) {
    console.error(
      `Item read-only property '${itemReadonlyProperty.toString()}' is not available for all items.`,
    );
  }
  if (
    itemReadonlyProperty &&
    items?.some(
      (item) =>
        itemReadonlyProperty &&
        typeof item === "object" &&
        item !== null &&
        itemReadonlyProperty in item &&
        item[itemReadonlyProperty] !== null &&
        typeof item[itemReadonlyProperty] !== "boolean",
    )
  ) {
    console.error("read-only value is not boolean in one of the items.");
  }
  if (
    itemReadonlyProperty &&
    modelValue.value &&
    (typeof modelValue.value !== "object" ||
      (typeof modelValue.value === "object" &&
        modelValue.value !== null &&
        !(itemReadonlyProperty in modelValue.value)))
  ) {
    console.error(
      `Item read-only property '${itemReadonlyProperty.toString()}' is not available in the model.`,
    );
  }
  if (
    itemReadonlyProperty &&
    modelValue.value &&
    typeof modelValue.value === "object" &&
    modelValue.value !== null &&
    itemReadonlyProperty in modelValue.value &&
    modelValue.value[itemReadonlyProperty] !== null &&
    typeof modelValue.value[itemReadonlyProperty] !== "boolean"
  ) {
    console.error("read-only value is not boolean in model.");
  }
}

function onNumberOfVisibleItemsChanged(): void {
  setMaxHeight();
}

/** Watches the `selectedItem` attribute. */
function onSelectionChanged(): void {
  if (selectedItem.value) {
    modelValue.value = selectedItem.value;
    emit("select", selectedItem.value);
  }
  checkItemProperties();
}

function setMaxHeight(): void {
  if (!numberOfVisibleItems || (items && numberOfVisibleItems >= items?.length)) {
    listBoxRef.value?.style.removeProperty("maxHeight");
    return;
  }
  let maxHeight = 0;
  for (let i = 0; i < numberOfVisibleItems; i++) {
    const listItem = listItemsRef.value ? listItemsRef.value[i] : null;
    if (listItem) {
      maxHeight += listItem.offsetHeight;
    }
  }
  if (maxHeight > 0 && listBoxRef.value) {
    listBoxRef.value.style.maxHeight = `${maxHeight}px`;
  }
}

onMounted(() => {
  if (allowSelect && autoSelect) {
    selectedIdx.value = 0;
  }
  setMaxHeight();
  checkItemProperties();
});

watch(() => numberOfVisibleItems, onNumberOfVisibleItemsChanged);
watch(selectedItem, onSelectionChanged);

defineExpose({
  modelValue,
  selectedIdx,
  listBoxRef,
  listItemsRef,
  selectedItem,
  selectItem,
  moveSelection,
  removeSelection,
});
</script>

<template>
  <ul
    tabindex="0"
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    ref="listBoxRef"
    class="bissantzControls listBoxControl"
    v-bind:class="{
      bordered: bordered,
      divided: itemDivider,
      dense: dense,
      disabled: disabled,
      fullWidth: fullWidth,
      fullHeight: fullHeight,
      readonly: readonly,
      rounded: rounded,
      outlined: outlined,
      scroll: items && numberOfVisibleItems && numberOfVisibleItems < items.length,
    }"
    v-on:keydown.up.stop.prevent="moveSelection(-1)"
    v-on:keydown.down.stop.prevent="moveSelection(1)"
    v-on:keydown.esc.stop.prevent="removeSelection"
    v-on:keydown.delete.stop.prevent="deleteItem"
  >
    <li
      ref="listItemsRef"
      class="listItem"
      v-for="(item, index) in items"
      v-bind:key="calculateKey(item) !== undefined ? calculateKey(item) : index"
      v-bind:class="{
        selected: allowSelect && selectedIdx === index,
        disabled: getDisabled(item),
        readonly: getReadonly(item),
        inactive: getInactive(item),
      }"
      v-on:click="selectItem(index)"
    >
      <span class="label">
        <slot
          name="list-item"
          v-bind:listItem="item"
          v-bind:index="index"
          v-bind:itemKey="calculateKey(item)"
          v-bind:itemText="calculateText(item)"
          >{{ calculateText(item) }}</slot
        >
      </span>
      <span
        v-if="allowOrder && !disabled && !getDisabled(item) && items && items.length > 1"
        v-on:click.stop
        class="orderContainer"
      >
        <BcButton
          v-if="index > 0"
          class="orderButton"
          tabindex="-1"
          outlined
          v-bind:rounded="rounded"
          v-on:click="changeOrder($event, index, -1)"
        >
          &#8593;
        </BcButton>
        <BcButton
          v-if="index < items.length - 1"
          class="orderButton"
          tabindex="-1"
          outlined
          v-bind:rounded="rounded"
          v-on:click="changeOrder($event, index, 1)"
        >
          &#8595;
        </BcButton>
      </span>
    </li>
  </ul>
</template>

<style lang="less">
@import "../styles/components/bc-list-box.less";
</style>
